import { capitalizeTerm } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { memberSinceLabel, profileErrorText, profileInitials } from "../lib/profile.js";
import { lootMeQueryOptions, profileQueryOptions } from "../lib/queries.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";
import { ErrorBanner } from "./ErrorBanner.js";

/**
 * A player as other shoppers see them (boxed's profile quick view, without the pictures, streaks,
 * highlights and binders loot-os has no data for): the lettered disc and display name a seat
 * shows, when they joined, and three counts. The viewer's own profile says so and links to the
 * Account page, where the username is changed.
 */
export function ProfileDialog({
  endUserId,
  onClose,
}: {
  /** The profile to show; null closes the dialog. */
  endUserId: string | null;
  onClose: () => void;
}) {
  const { box, battle } = useTerminology();
  const open = endUserId !== null;
  const { sessionToken } = useShopper();
  const profile = useQuery({ ...profileQueryOptions(endUserId ?? ""), enabled: open });
  const me = useQuery({ ...lootMeQueryOptions(), enabled: open && !!sessionToken });
  const isMe = open && me.data?.id === endUserId;
  const data = profile.data;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{data?.displayName ?? "Player profile"}</DialogTitle>
        </DialogHeader>
        {data ? (
          <div className="grid gap-4">
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="inline-flex size-14 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xl font-semibold text-primary"
              >
                {profileInitials(data.displayName)}
              </span>
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-lg font-semibold">
                  <span className="truncate">{data.displayName}</span>
                  {isMe ? <Badge variant="outline">You</Badge> : null}
                </p>
                <p className="text-sm text-muted-foreground">
                  Member since {memberSinceLabel(data.memberSince)}
                </p>
              </div>
            </div>
            <dl className="grid grid-cols-3 gap-2">
              <Stat label={`${capitalizeTerm(box.plural)} opened`} value={data.stats.boxesOpened} />
              <Stat
                label={`${capitalizeTerm(battle.plural)} played`}
                value={data.stats.battlesPlayed}
              />
              <Stat label={`${capitalizeTerm(battle.plural)} won`} value={data.stats.battlesWon} />
            </dl>
          </div>
        ) : profile.error ? (
          <ErrorBanner>{profileErrorText(profile.error)}</ErrorBanner>
        ) : (
          <div className="grid gap-4">
            <div className="flex items-center gap-3">
              <Skeleton className="size-14 rounded-full" />
              <div className="grid flex-1 gap-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-44" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          </div>
        )}
        <DialogFooter>
          {isMe ? (
            <Link
              to="/account"
              onClick={onClose}
              className={buttonVariants({ variant: "outline" })}
            >
              Account
            </Link>
          ) : null}
          <DialogClose render={<Button type="button" variant="secondary" />}>Close</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-center">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-mono text-lg font-semibold">{value.toLocaleString()}</dd>
    </div>
  );
}
